import {
  buildReadOnlyChatMessage,
  buildClosedChatMessage,
  CLINICAL_ITEM_TYPES_THAT_SKIP_WARNING_MODAL,
  ORGAN_FEEDBACK_EMAILS,
  SHARED_MESSAGE_KEYS,
  DEFAULT_FEEDBACK_EMAIL,
} from '@features/components/Chat/constants';
import type { Channel, LocalMessage } from 'stream-chat';
import { ClinicalItemType, DonorType, OrganType } from '@/api/apis/case';
import { RenderChannelsOptions, StreamUserRole } from './types';
import { ChatAccess, ChatAccessByRole } from './constants';
import { StreamChannelType } from '@/api/apis/chat';

export function hasChatsFullAccess(userRole?: StreamUserRole | string | null): boolean {
  return userRole == null || ChatAccessByRole[userRole as StreamUserRole] === ChatAccess.full;
}

export function shouldSkipWarningModal(clinicalItemType?: ClinicalItemType): boolean {
  if (!clinicalItemType) return false;
  return CLINICAL_ITEM_TYPES_THAT_SKIP_WARNING_MODAL.has(clinicalItemType);
}

function matchChannelField<T>(filterValues: T[], channelValue: T): boolean {
  return filterValues.length ? filterValues.includes(channelValue) : true;
}

export const isChannelInCase = (channel?: Channel | null, caseId?: string): boolean => {
  if (!caseId) return true;
  return (channel?.data?.caseIds as string[] | undefined)?.includes(caseId) ?? false;
};

export const filterChannelsToRender = (
  channels: Channel[],
  { caseId, uiFilters, chatTypes }: RenderChannelsOptions = {},
) => {
  return channels.filter((c) => {
    if (!matchChannelField(chatTypes ?? [], c.data?.type as StreamChannelType)) return false;

    if (caseId) return isChannelInCase(c, caseId);
    if (!uiFilters) return true;

    const matchesDonor = matchChannelField(uiFilters.donorTypes, c.data?.donor_type as DonorType);
    const matchesOrgan = matchChannelField(uiFilters.organTypes, c.data?.organ_type as OrganType);
    const matchesChatType = matchChannelField(uiFilters.chatTypes, c.data?.type as StreamChannelType);
    return matchesDonor && matchesOrgan && matchesChatType;
  });
};

export function isClinicalDataSharedMessage(message: Partial<LocalMessage>): boolean {
  return SHARED_MESSAGE_KEYS.some((key) => key in message);
}

export function isChannelFrozen(channel?: Channel | null): boolean {
  return Boolean(channel?.data?.frozen);
}

export function isChannelReadOnly(channel?: Channel | null): boolean {
  const capabilities = channel?.data?.own_capabilities;
  return (
    channel?.state?.membership?.channel_role === 'channel_member_readonly' ||
    (Array.isArray(capabilities) && !capabilities.includes('send-message'))
  );
}

export function renderFilteredChannels(channels: Channel[], options: RenderChannelsOptions = {}): Channel[] {
  const { uiFilters, inlineLayout } = options;
  let result = filterChannelsToRender(channels, options);
  if (inlineLayout && uiFilters?.unreadOnly) {
    result = result.filter((c) => c.countUnread() > 0);
  }
  return result;
}

export const resolveOrganEmail = (organ?: string): string => {
  const key = organ?.trim().toLowerCase();
  if (!key) return DEFAULT_FEEDBACK_EMAIL;
  if (key.startsWith('heart')) return ORGAN_FEEDBACK_EMAILS.Heart;
  if (key.startsWith('lung')) return ORGAN_FEEDBACK_EMAILS.Lung;
  if (key.startsWith('liver')) return ORGAN_FEEDBACK_EMAILS.Liver;
  return DEFAULT_FEEDBACK_EMAIL;
};

export const getClosedChatMessageForChannel = (channel?: Channel | null): string => {
  const organ = (channel?.data?.organ_type as string | undefined) ?? (channel?.data?.organ as string | undefined);

  const email = resolveOrganEmail(organ);
  if (channel && isChannelReadOnly(channel)) {
    return buildReadOnlyChatMessage();
  }

  return buildClosedChatMessage(email);
};
